import { useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { parseDocumentPdf, type ParsedDoc, type DocCategory } from "../lib/documents/parseDocument";
import { ingestDocument } from "../lib/documents/ingest";

/**
 * Feed the Eater documents — browser parse (pdf.js + OCR for scans) + redact
 * (account/SSN/EIN) + categorize, then commit to ingest-document-corpus.
 * Documentary evidence: trust/corporate, financial, public/regulatory.
 */
const CATS: { value: DocCategory; label: string }[] = [
  { value: "trust_corporate", label: "Trust, Will & Corporate" },
  { value: "financial", label: "Accounting & Financial" },
  { value: "public_regulatory", label: "Public Records & Regulatory" },
  { value: "document", label: "Other Document" },
];

interface Parsed { file: File; doc: ParsedDoc; cat: DocCategory }
interface Committed { label: string; ok: boolean; detail: string }

export default function UploadDocuments() {
  const [params] = useSearchParams();
  const caseId = params.get("case") ?? undefined;
  const navigate = useNavigate();

  const [batchCat, setBatchCat] = useState<DocCategory>("trust_corporate");
  const [queue, setQueue] = useState<File[]>([]);
  const [parsing, setParsing] = useState(false);
  const [progress, setProgress] = useState<{ i: number; n: number; phase: string }>({ i: 0, n: 0, phase: "" });
  const [parsed, setParsed] = useState<Parsed[]>([]);
  const [committing, setCommitting] = useState(false);
  const [results, setResults] = useState<Committed[]>([]);

  const onPick = useCallback((files: FileList | null) => {
    if (!files) return;
    setQueue((q) => [...q, ...Array.from(files).filter((f) => f.name.toLowerCase().endsWith(".pdf"))]);
    setResults([]);
  }, []);

  async function parseAll() {
    setParsing(true); setParsed([]); setResults([]);
    const out: Parsed[] = [];
    for (let i = 0; i < queue.length; i++) {
      const f = queue[i]!;
      try {
        const doc = await parseDocumentPdf(f, batchCat, null, {
          onProgress: (phase, _pct) => setProgress({ i: i + 1, n: queue.length, phase: `${f.name.slice(0, 28)} — ${phase}` }),
        });
        out.push({ file: f, doc, cat: batchCat });
      } catch (e) {
        out.push({ file: f, doc: { source_label: f.name, category: batchCat, instrument_type: null, doc_date: null, page_count: 0, redaction_total: 0, redaction_types: {}, redaction_originals: [], pages: [] }, cat: batchCat });
        setResults((r) => [...r, { label: f.name, ok: false, detail: e instanceof Error ? e.message : String(e) }]);
      }
    }
    setParsed(out);
    setParsing(false);
  }

  function setCat(idx: number, cat: DocCategory) {
    setParsed((p) => p.map((x, i) => i === idx ? { ...x, cat, doc: { ...x.doc, category: cat } } : x));
  }

  async function commitAll() {
    if (!caseId) return;
    setCommitting(true); setResults([]);
    for (const p of parsed) {
      if (p.doc.pages.length === 0) continue;
      try {
        await ingestDocument(caseId, p.doc, p.file);
        setResults((r) => [...r, { label: p.doc.source_label, ok: true, detail: `${p.doc.page_count}pp · ${p.doc.redaction_total} redacted` }]);
      } catch (e) {
        setResults((r) => [...r, { label: p.doc.source_label, ok: false, detail: e instanceof Error ? e.message : String(e) }]);
      }
    }
    setCommitting(false);
  }

  const totalRedactions = parsed.reduce((s, p) => s + p.doc.redaction_total, 0);

  return (
    <div className="mx-auto min-h-screen max-w-4xl px-6 py-10 text-bone">
      <button onClick={() => caseId ? navigate(`/case/${caseId}/hounds`) : navigate("/")}
        className="mb-4 font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-ember-bright">← back to the eater</button>

      <p className="font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">// feed the eater · documents</p>
      <h1 className="mt-2 font-display text-2xl font-light text-bone">Documentary evidence</h1>
      <p className="mt-2 max-w-2xl font-display text-sm text-bone-dim">
        Trust/corporate, financial, and public/regulatory PDFs. Parsed in your browser
        (scans are OCR'd), with account numbers, SSNs, and EINs auto-redacted before anything is sent.
        Licenses, permits, and DEA numbers are preserved.
      </p>
      {!caseId && <p className="mt-3 font-display text-base text-ember-bright">// open this from a case's Hell Hounds panel (no case bound)</p>}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute">category</label>
        <select value={batchCat} onChange={(e) => setBatchCat(e.target.value as DocCategory)}
          className="rounded border border-steel-dark bg-charcoal/80 px-3 py-1.5 font-display text-sm text-bone focus:border-ember-bright/50 focus:outline-none">
          {CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <label className="cursor-pointer rounded border border-dashed border-ember-bright/40 bg-iron/40 px-4 py-2 font-display text-base font-light uppercase tracking-[0.2em] text-bone hover:border-ember-bright/60 hover:bg-ember/20">
          ⊕ choose PDFs
          <input type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => onPick(e.target.files)} />
        </label>
        {queue.length > 0 && !parsing && parsed.length === 0 && (
          <button onClick={parseAll} className="rounded border border-ember-bright/50 bg-ember/20 px-4 py-2 font-display text-base font-light uppercase tracking-[0.2em] text-ember-bright hover:bg-ember/40">
            parse {queue.length} doc{queue.length === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {queue.length > 0 && parsed.length === 0 && (
        <p className="mt-3 font-display text-base text-bone-dim">{queue.length} queued: {queue.slice(0, 8).map((f) => f.name).join(", ")}{queue.length > 8 ? ` …+${queue.length - 8}` : ""}</p>
      )}

      {parsing && (
        <div className="mt-5 rounded border border-steel-dark bg-iron/70 px-5 py-4">
          <p className="font-display text-base font-light uppercase tracking-[0.2em] text-ember-bright">// eater // {progress.i} of {progress.n} · {progress.phase}</p>
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-steel-dark">
            <div className="h-full bg-ember-bright transition-all" style={{ width: `${progress.n ? (progress.i / progress.n) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {parsed.length > 0 && (
        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <p className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">
              {parsed.length} parsed · {totalRedactions} redactions
            </p>
            <button onClick={commitAll} disabled={!caseId || committing}
              className="rounded border border-ember-bright/60 bg-ember/20 px-4 py-2 font-display text-base font-light uppercase tracking-[0.14em] text-ember-bright hover:bg-ember/40 disabled:opacity-40">
              {committing ? "committing…" : `commit ${parsed.length} to corpus →`}
            </button>
          </div>
          <ul className="space-y-2">
            {parsed.map((p, idx) => {
              const result = results.find((r) => r.label === p.doc.source_label);
              const modes = p.doc.pages.reduce((m, pg) => { m[pg.mode] = (m[pg.mode] ?? 0) + 1; return m; }, {} as Record<string, number>);
              return (
                <li key={idx} className="rounded border border-steel-dark/70 bg-black/20 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="truncate font-display text-[13px] text-bone">{p.doc.source_label.replace(/\.pdf$/i, "")}</span>
                    <select value={p.cat} onChange={(e) => setCat(idx, e.target.value as DocCategory)}
                      className="rounded border border-steel-dark bg-charcoal/80 px-2 py-1 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-dim focus:outline-none">
                      {CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <p className="mt-1 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">
                    {p.doc.page_count}pp · {modes.ocr ? `${modes.ocr} OCR'd` : "text"} · {p.doc.redaction_total} redacted
                    {Object.entries(p.doc.redaction_types).map(([t, n]) => ` · ${n} ${t}`).join("")}
                  </p>
                  {result && (
                    <p className={`mt-1 font-display text-sm font-light uppercase tracking-[0.08em] ${result.ok ? "text-ember-bright" : "text-ember-bright"}`}>
                      {result.ok ? "✓ committed" : "✕ failed"} — {result.detail}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-3 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/60">
            // after committing, sound the horn to weave them into the timeline
          </p>
        </div>
      )}
    </div>
  );
}
